import { useEffect, useState } from "react";
import { useGodown, useCreateGodown, useUpdateGodown } from "http-hooks/godown/useGodown";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import SelectStates from "components/ui/selectors/SelectStates";
import Modal from "components/ui/modals";

const statusOptions = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

export default function SetGodown({ id, onClose, open }) {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    state: "",
    agstat: 1,
  });
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [selectedState, setSelectedState] = useState(null);

  const { data: godown, isLoading: isLoadingGodown } = useGodown(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateGodown();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateGodown();

  const isPending = isCreating || isUpdating;

  useEffect(() => {
    if (godown && id) {
      setFormData({
        name: godown.name || "",
        address: godown.address || "",
        state: godown.state || "",
        agstat: godown.agstat !== undefined ? godown.agstat : 1,
      });
      setSelectedStatus(statusOptions.find((opt) => opt.value === godown.agstat) || statusOptions[0]);
      if (godown.state) {
        setSelectedState({ value: godown.state, label: godown.state });
      }
    }
  }, [godown, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await mutateUpdate({ id, data: formData });
      } else {
        await mutateCreate(formData);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setFormData((prev) => ({ ...prev, agstat: selectedOption?.value || 1 }));
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setFormData((prev) => ({ ...prev, state: selectedOption?.value || "" }));
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Edit Godown" : "Add Godown"}
        </h3>
      }
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-godown-form"
            disabled={isPending}
            className="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : id ? "Update" : "Create"}
          </button>
        </div>
      }
    >
      {id && isLoadingGodown ? (
        <div className="animate-pulse space-y-4">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-16 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
        </div>
      ) : (
        <form id="set-godown-form" onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Godown Name *"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter godown name"
            maxLength={50}
            required
          />
          <Input
            label="Address *"
            type="textarea"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="Enter address"
            maxLength={200}
            rows={3}
            required
          />
          <SelectStates
            label="State *"
            value={selectedState}
            onChange={handleStateChange}
            isClearable={false}
            isSearchable={true}
          />
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={handleStatusChange}
            label="Status *"
            placeholder="Select status"
            isClearable={false}
            isSearchable={false}
          />
        </form>
      )}
    </Modal>
  );
}
